import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { StatementRenderer } from "@/payroll/ai-script/statement/statement-renderer";
import type { PollingResult } from "./polling";
import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementProjectionBuild,
} from "./types";

const CenteredStatementState = ({
  title,
  body,
  loading = false,
}: {
  title: string;
  body: string;
  loading?: boolean;
}) => (
  <Stack
    fullWidth
    align="center"
    justify="center"
    gap="xs"
    className="h-full px-6 text-center"
  >
    {loading && <LoadingSpinner containerClassName="h-auto" />}
    <Typography variant="subtitleW2">{title}</Typography>
    <Typography variant="bodyW3" textColor="secondary">
      {body}
    </Typography>
  </Stack>
);

export const StatementPreview = ({
  recipient,
  projectionBuild,
}: {
  recipient: PayrollStatementNotificationRecipient;
  projectionBuild: PollingResult<PayrollStatementProjectionBuild> | null;
}) => {
  if (
    projectionBuild?.status === "pending" &&
    !projectionBuild.polling_timed_out
  ) {
    return (
      <CenteredStatementState
        loading
        title="Building this period's PayPacks"
        body="This preview will appear automatically when the shared build finishes."
      />
    );
  }
  if (projectionBuild?.polling_timed_out) {
    return (
      <CenteredStatementState
        title="PayPack build is taking longer than expected"
        body="Use Check again above the recipient list to resume checking its progress."
      />
    );
  }
  if (projectionBuild?.status === "failed") {
    return (
      <CenteredStatementState
        title="PayPack build failed"
        body={
          projectionBuild.error ?? "Retry the shared PayPack build to continue."
        }
      />
    );
  }
  if (recipient.statement_projection_error) {
    return (
      <CenteredStatementState
        title="PayPack unavailable"
        body={recipient.statement_projection_error}
      />
    );
  }
  if ((recipient.statement_audit_issues?.length ?? 0) > 0) {
    return (
      <CenteredStatementState
        title="PayPack needs review"
        body={recipient.statement_audit_issues?.join("; ") ?? "Invalid PayPack"}
      />
    );
  }
  if (!recipient.statement_preview_available) {
    return (
      <CenteredStatementState
        title="No PayPack to preview"
        body="This technician doesn't have a valid PayPack for this period."
      />
    );
  }

  const statement = recipient.statement;
  if (!statement || statement.blocks.length === 0) {
    return (
      <CenteredStatementState
        title="Nothing to show"
        body="This technician's PayPack came back empty for this period."
      />
    );
  }

  return <StatementRenderer statement={statement} />;
};
